import { useState } from 'react';
import type { PermissionKey, PublicUser } from '@otter/shared';
import { hasPermission } from '../../lib/perm';
import { Apariencia } from './Apariencia';
import { Comerciales } from './Comerciales';
import { Emisor } from './Emisor';
import { Fiscal } from './Fiscal';
import { Roles } from './Roles';
import { Serie } from './Serie';
import { TesoreriaSettings } from './TesoreriaSettings';

type Section = 'apariencia' | 'usuarios' | 'roles' | 'emisor' | 'serie' | 'tesoreria' | 'fiscal';

type Props = {
  user: PublicUser;
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (e: unknown) => void;
};

const SECTION_PERM: Record<Section, PermissionKey | null> = {
  apariencia: null, // per-user theme, always available
  usuarios: 'admin.users.manage',
  roles: 'admin.roles.manage',
  emisor: 'admin.issuer.manage',
  serie: 'admin.series.manage',
  tesoreria: 'admin.settings.manage',
  fiscal: 'admin.settings.manage',
};

export function Admin({ user, onToast, onError }: Props) {
  const allowed = (Object.keys(SECTION_PERM) as Section[]).filter((s) => {
    const p = SECTION_PERM[s];
    return p === null ? true : hasPermission(user, p);
  });
  const [sec, setSec] = useState<Section>(allowed[0] ?? 'apariencia');
  if (allowed.length === 0) {
    return (
      <div className="body">
        <div className="empty">No tienes permisos para acceder a Administración.</div>
      </div>
    );
  }
  if (!allowed.includes(sec)) {
    setSec(allowed[0]!);
    return null;
  }
  return (
    <div className="body no-pad">
      <div className="ftabs">
        {allowed.map((s) => (
          <button key={s} className={sec === s ? 'on' : ''} onClick={() => setSec(s)}>
            {s}
          </button>
        ))}
      </div>
      {sec === 'apariencia' && <Apariencia onToast={onToast} onError={onError} />}
      {sec === 'usuarios' && <Comerciales actor={user} onToast={onToast} onError={onError} />}
      {sec === 'roles' && <Roles onToast={onToast} onError={onError} />}
      {sec === 'emisor' && <Emisor onToast={onToast} onError={onError} />}
      {sec === 'serie' && <Serie onToast={onToast} onError={onError} />}
      {sec === 'tesoreria' && <TesoreriaSettings onToast={onToast} onError={onError} />}
      {sec === 'fiscal' && <Fiscal onToast={onToast} onError={onError} />}
    </div>
  );
}
